import React, { useEffect, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    profileChangePassword,
    setCabinetProfileChangePasswordModalStatus
} from "../../../store/cabinet/action";
import { toast } from "react-toastify";

import TwoFactorCodeInput from "../../_UI/twoFactorCodeInput";

import passwordIcon from "../../../theme/assets/svg/b_cabinet_profile_2fa_password_icon.svg";

const CODE_LENGTH = 6;
const PASSWORD_CHANGE_2FA_SCOPE = "password_change";

const initialFormData = {
    current_password: "",
    password: "",
    password_confirmation: "",
};

const getErrorSource = (formErr) => {
    return (
        formErr?.response?.data?.errors ||
        formErr?.data?.errors ||
        formErr?.errors ||
        formErr ||
        {}
    );
};

const getFieldError = (formErr, fieldName) => {
    const errors = getErrorSource(formErr);
    const fieldError = errors?.[fieldName];

    if (!fieldError) return "";

    if (Array.isArray(fieldError)) {
        return fieldError[0] || "";
    }

    return String(fieldError);
};

const getGeneralError = (formErr) => {
    return (
        formErr?.response?.data?.message ||
        formErr?.data?.message ||
        formErr?.message ||
        ""
    );
};

const getTwoFactorScopes = (options) => {
    if (Array.isArray(options)) return options;
    if (Array.isArray(options?.scopes)) return options.scopes;

    return [];
};

const isTwoFactorScopeEnabled = (options, scope) => {
    if (!options?.enabled) return false;

    return getTwoFactorScopes(options).includes(scope);
};

const CabinetProfileChangePasswordModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const modalStatus = useSelector(store => store.cabinet.cabinetProfileChangePasswordModalStatus);
    const profileChangePasswordStatusStarted = useSelector(store => store.cabinet.profileChangePasswordStatusStarted);
    const formErr = useSelector(store => store.cabinet.profileChangePasswordStatusErr);
    const profileMainData = useSelector(store => store.cabinet.profileMainData);

    const [formData, setFormData] = useState(initialFormData);
    const [verificationCode, setVerificationCode] = useState("");
    const [formError, setFormError] = useState("");
    const [hiddenErrors, setHiddenErrors] = useState({});
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const twoFactorOptions = profileMainData?.two_factor;
    const isPasswordChange2faRequired = isTwoFactorScopeEnabled(
        twoFactorOptions,
        PASSWORD_CHANGE_2FA_SCOPE
    );

    const isPasswordMismatch =
        formData.password_confirmation &&
        formData.password !== formData.password_confirmation;

    const isConfirmDisabled =
        profileChangePasswordStatusStarted ||
        isPasswordMismatch ||
        (isPasswordChange2faRequired && verificationCode.length !== CODE_LENGTH);

    const closeModal = () => {
        if (profileChangePasswordStatusStarted) return;

        dispatch(setCabinetProfileChangePasswordModalStatus(false));
    };

    const successHandler = () => {
        closeModal();
        toast(t("Ваш пароль успешно изменен!"));
    };

    const inputChangeHandler = (event) => {
        const { name, value } = event.target;

        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));

        setFormError("");

        setHiddenErrors((prevState) => ({
            ...prevState,
            [name]: true,
        }));
    };

    const codeChangeHandler = (value) => {
        setVerificationCode(value);
        setFormError("");

        setHiddenErrors((prevState) => ({
            ...prevState,
            code: true,
        }));
    };

    const hasFieldError = (fieldName) => {
        if (!hasSubmitted || profileChangePasswordStatusStarted) return false;

        return !!getFieldError(formErr, fieldName) && !hiddenErrors[fieldName];
    };

    const renderFieldError = (fieldName) => {
        if (!hasSubmitted || profileChangePasswordStatusStarted) return null;

        const error = getFieldError(formErr, fieldName);

        if (!error || hiddenErrors[fieldName]) return null;

        return (
            <div className={Styles.formError}>
                {t(error)}
            </div>
        );
    };

    const renderGeneralError = () => {
        if (!hasSubmitted || profileChangePasswordStatusStarted) return null;

        const generalError = getGeneralError(formErr);
        const errors = getErrorSource(formErr);
        const hasFieldErrors = Object.keys(errors || {}).length > 0;

        if (!generalError || hasFieldErrors) return null;

        return (
            <div className={Styles.generalError}>
                {t(generalError)}
            </div>
        );
    };

    const confirmHandler = () => {
        if (profileChangePasswordStatusStarted) return;

        setHasSubmitted(true);
        setHiddenErrors({});
        setFormError("");

        if (isPasswordMismatch) {
            setFormError(t("Пароли не совпадают"));
            return;
        }

        if (isPasswordChange2faRequired && verificationCode.length !== CODE_LENGTH) {
            setFormError(t("Введите 6-значный код из приложения-аутентификатора"));
            return;
        }

        const payload = {
            current_password: formData.current_password,
            password: formData.password,
            password_confirmation: formData.password_confirmation,
        };

        if (isPasswordChange2faRequired) {
            payload.code = verificationCode;
        }

        dispatch(profileChangePassword(payload, successHandler));
    };

    useEffect(() => {
        if (!modalStatus) {
            setFormData(initialFormData);
            setVerificationCode("");
            setFormError("");
            setHiddenErrors({});
            setHasSubmitted(false);
        }
    }, [modalStatus]);

    useEffect(() => {
        if (formErr) {
            setHiddenErrors({});
        }
    }, [formErr]);

    return (
        <Popup
            open={!!modalStatus}
            modal
            nested
            closeOnDocumentClick={!profileChangePasswordStatusStarted}
            onClose={closeModal}
            className="cabinetProfileChangePasswordModal"
        >
            <div className={Styles.modalContainer}>
                <div
                    className={`${Styles.modalCloseBtn} ${profileChangePasswordStatusStarted ? Styles.modalCloseBtnDisabled : ""}`}
                    onClick={closeModal}
                >
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div className={Styles.titleRow}>
                        <div className={Styles.titleIcon}>
                            <img src={passwordIcon} alt={t("Изменить пароль")} />
                        </div>

                        <div className={Styles.titleTextWrap}>
                            <div className={Styles.title}>
                                {t("Изменить пароль")}
                            </div>

                            <div className={Styles.note}>
                                {isPasswordChange2faRequired
                                    ? t("Введите пароль и подтвердите действие кодом 2FA")
                                    : t("Безопасность аккаунта")}
                            </div>
                        </div>
                    </div>

                    <div className={Styles.formWrap}>
                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="profile-current-password"
                            >
                                {t("Ваш старый пароль")}
                            </label>

                            <input
                                id="profile-current-password"
                                type="password"
                                name="current_password"
                                value={formData.current_password}
                                onChange={inputChangeHandler}
                                className={`
                                    ${Styles.formInput}
                                    ${hasFieldError("current_password") ? Styles.formInputError : ""}
                                `}
                                placeholder={t("Введите старый пароль")}
                                autoComplete="current-password"
                                disabled={profileChangePasswordStatusStarted}
                            />

                            {renderFieldError("current_password")}
                        </div>

                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="profile-new-password"
                            >
                                {t("Новый пароль")}
                            </label>

                            <input
                                id="profile-new-password"
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={inputChangeHandler}
                                className={`
                                    ${Styles.formInput}
                                    ${hasFieldError("password") ? Styles.formInputError : ""}
                                `}
                                placeholder={t("Введите новый пароль")}
                                autoComplete="new-password"
                                disabled={profileChangePasswordStatusStarted}
                            />

                            {renderFieldError("password")}
                        </div>

                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="profile-repeat-password"
                            >
                                {t("Повторите новый пароль")}
                            </label>

                            <input
                                id="profile-repeat-password"
                                type="password"
                                name="password_confirmation"
                                value={formData.password_confirmation}
                                onChange={inputChangeHandler}
                                className={`
                                    ${Styles.formInput}
                                    ${(isPasswordMismatch || hasFieldError("password_confirmation")) ? Styles.formInputError : ""}
                                `}
                                placeholder={t("Введите новый пароль ещё раз")}
                                autoComplete="new-password"
                                disabled={profileChangePasswordStatusStarted}
                            />

                            {isPasswordMismatch ? (
                                <div className={Styles.formError}>
                                    {t("Пароли не совпадают")}
                                </div>
                            ) : (
                                renderFieldError("password_confirmation")
                            )}
                        </div>
                    </div>

                    {isPasswordChange2faRequired && (
                        <TwoFactorCodeInput
                            value={verificationCode}
                            onChange={codeChangeHandler}
                            formErr={formErr}
                            errorFieldName="code"
                            isLoading={profileChangePasswordStatusStarted}
                            label="Код аутентификации"
                            hint="Откройте Google Authenticator и введите код, который сейчас отображается для Kairo."
                        />
                    )}

                    {formError && (
                        <div className={Styles.generalError}>
                            {formError}
                        </div>
                    )}

                    {renderGeneralError()}

                    <button
                        type="button"
                        className={Styles.confirmBtn}
                        onClick={confirmHandler}
                        disabled={isConfirmDisabled}
                    >
                        {profileChangePasswordStatusStarted ? t("Сохранение") : t("Подтвердить")}
                    </button>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetProfileChangePasswordModal;
